import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Quote, ImagePlus } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { BeforeAfterSlider } from '../simulation/BeforeAfterSlider';
import { SectionHeading } from './SectionHeading';
import { Container } from './PublicSections';
import { SHOWCASE_EXAMPLES, SHOWCASE_TESTIMONIALS, SHOWCASE_COPY } from '../../seo/showcase';

const IllustrativeBadge = ({ children, testId }) => (
  <Badge variant="outline" className="border-lux-gold/40 bg-lux-gold/10 text-[var(--lux-amber)] text-[10px] uppercase tracking-wider font-semibold rounded-full px-2.5 py-0.5" data-testid={testId}>
    <Sparkles className="w-3 h-3 mr-1" /> {children}
  </Badge>
);

const ExampleCard = ({ example, lang, copy }) => {
  const c = example[lang];
  return (
    <Card className="bg-lux-panel/90 border-white/10 overflow-hidden" data-testid={`showcase-example-${example.id}`}>
      <CardContent className="p-4 sm:p-5 space-y-4">
        <BeforeAfterSlider
          beforeSrc={example.before}
          afterSrc={example.after}
          language={lang}
          aspectRatio="3 / 4"
          fit="cover"
          hint={copy.hint}
          testId={`showcase-slider-${example.id}`}
          beforeAlt={copy.beforeAlt}
          afterAlt={copy.afterAlt}
        />
        <div className="space-y-2">
          {example.illustrative && <IllustrativeBadge testId={`showcase-badge-${example.id}`}>{copy.illustrativeBadge}</IllustrativeBadge>}
          <h3 className="text-base font-semibold text-white">{c.title}</h3>
          <p className="text-sm text-white/55 leading-relaxed">{c.text}</p>
          <p className="text-xs text-white/40"><span className="uppercase tracking-wider">{copy.axesLabel}</span> : {c.axes.join(' \u00b7 ')}</p>
        </div>
      </CardContent>
    </Card>
  );
};

/** Placeholder reserved for real anonymized results; doubles as a CTA so it never looks unfinished. */
const RealPhotoSlot = ({ copy, appHref }) => (
  <Card className="border-dashed border-lux-gold/30 bg-lux-gold/5 overflow-hidden flex" data-testid="showcase-real-slot">
    <CardContent className="p-6 flex flex-col justify-center items-start gap-4 w-full">
      <div className="w-full rounded-xl border border-dashed border-white/15 bg-black/20 flex items-center justify-center" style={{ aspectRatio: '3 / 4', maxHeight: 360 }} aria-hidden="true">
        <ImagePlus className="w-10 h-10 text-white/15" />
      </div>
      <h3 className="text-base font-semibold text-white">{copy.slotTitle}</h3>
      <p className="text-sm text-white/55 leading-relaxed">{copy.slotText}</p>
      <Button asChild className="h-10 bg-[var(--lux-gold)] text-[var(--lux-ink)] hover:bg-[var(--lux-gold-2)] font-semibold active:scale-[0.98] transition-[background-color,transform] duration-200" data-testid="showcase-slot-cta">
        <Link to={appHref}>{copy.slotCta} <ArrowRight className="w-4 h-4 ml-2" /></Link>
      </Button>
    </CardContent>
  </Card>
);

const TestimonialCard = ({ item, lang, copy }) => {
  const c = item[lang];
  return (
    <li className="rounded-2xl border border-white/10 bg-lux-panel/90 p-6 flex flex-col gap-4" data-testid={`testimonial-${item.id}`}>
      <Quote className="w-6 h-6 text-[var(--lux-gold)]" aria-hidden="true" />
      <blockquote className="text-sm text-white/75 leading-relaxed flex-1">{'\u201c'}{c.text}{'\u201d'}</blockquote>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-semibold text-white">{item.name}, {item.age} {copy.yearsOld}</p>
          <p className="text-xs text-white/40">{c.focus}</p>
        </div>
        {item.illustrative && <IllustrativeBadge testId={`testimonial-badge-${item.id}`}>{copy.testimonialBadge}</IllustrativeBadge>}
      </div>
    </li>
  );
};

export const ShowcaseSection = ({ lang, appHref }) => {
  const copy = SHOWCASE_COPY[lang];
  const anyIllustrativeExample = SHOWCASE_EXAMPLES.some(e => e.illustrative);
  const anyIllustrativeTestimonial = SHOWCASE_TESTIMONIALS.some(t => t.illustrative);
  return (
    <section className="py-16 sm:py-24 border-t border-white/[0.06]" aria-labelledby="showcase-title" data-testid="showcase-section">
      <Container>
        <SectionHeading title={copy.title} sub={copy.sub} testId="showcase-heading" />
        {anyIllustrativeExample && (
          <p className="mt-4 max-w-3xl text-xs text-white/45 leading-relaxed" data-testid="showcase-illustrative-note">{copy.illustrativeNote}</p>
        )}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="showcase-grid">
          {SHOWCASE_EXAMPLES.map(ex => <ExampleCard key={ex.id} example={ex} lang={lang} copy={copy} />)}
          <RealPhotoSlot copy={copy} appHref={appHref} />
        </div>

        <div className="mt-16">
          <h3 className="font-display text-2xl sm:text-3xl text-white">{copy.testimonialsTitle}</h3>
          {anyIllustrativeTestimonial && <p className="mt-2 text-xs text-white/45" data-testid="testimonials-note">{copy.testimonialsNote}</p>}
          <ul className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="testimonials-list">
            {SHOWCASE_TESTIMONIALS.map(t => <TestimonialCard key={t.id} item={t} lang={lang} copy={copy} />)}
          </ul>
        </div>
      </Container>
    </section>
  );
};
